import { EcoInventDataset, useEcoInventDatasets } from "@/hooks/useEcoInventDatasets";
import { CloseOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { ReactNode, useEffect, useState } from "react";

import useIntercom from "@/hooks/useIntercom";
import { EcoInventDatasetTable } from "../ImpactReferenceSelector/EcoInventDatasetsTable";
import useTableStateParams from "../Table/hooks/useTableState";
import { Search } from "../Table/Table";
import styles from "./EcoInventDatasetSelector.module.scss";

export interface EcoInventDatasetSelectorProps {
  defaultSearch?: Search;
  onSelect?: (dataset: EcoInventDataset) => void;
  children: ReactNode;
}

export default function EcoInventDatasetSelector({
  onSelect,
  children,
  defaultSearch,
}: EcoInventDatasetSelectorProps) {
  const [selectedDataset, setSelectedDataset] = useState<EcoInventDataset>();
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const { setMarginBottom } = useIntercom();

  const { pagination, filter, search, sort, onSearchFilterSortPaginate } = useTableStateParams({
    defaultPagination: { page: 1, pageSize: 20 },
    defaultSort: { key: "reference_product_name", order: "ascend" },
    defaultSearch,
  });

  const { data, isLoading, isFetching } = useEcoInventDatasets({
    pagination,
    filter,
    search,
    sort,
  });

  const onOpen = () => {
    setIsVisible(true);
    setMarginBottom(85);
  };

  const onClose = () => {
    setIsVisible(false);
    setMarginBottom(0);
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, []);

  const onSubmit = () => {
    if (selectedDataset) {
      onSelect?.(selectedDataset);
      onClose();
    }
  };

  return (
    <>
      <span onClick={onOpen}>{children}</span>
      {isVisible && (
        <div className={styles.MaterialImpactReferenceModal}>
          <div className={styles.header}>
            <span>Select impact reference</span>
            <CloseOutlined
              className={styles.close}
              onClick={onClose}
            />
          </div>
          <div className={styles.container}>
            <EcoInventDatasetTable
              loading={isLoading || isFetching}
              data={data?.datasets}
              onSearchFilterSortPaginate={onSearchFilterSortPaginate}
              totalRows={data?.total_count}
              pagination={pagination}
              sort={sort}
              search={search}
              filter={filter}
              onRowClick={(record) => setSelectedDataset(record)}
              activeRowKey={selectedDataset?.id}
            />
          </div>
          <div className={styles.footer}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              disabled={!selectedDataset}
              type="primary"
              onClick={onSubmit}
            >
              Select
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
